import { createI18n } from "vue-i18n";
import { computed } from "vue";
import zhTw from "./lang/zh-tw";
import en from "./lang/en";
import { parseTranslationCenterResponse } from "~/utils/translate";
import {
	isI18nLocaleRoute,
	parseLocaleFromPath,
	switchLocaleInFullPath,
} from "~/utils/i18n-locale-routes";
import { localeToApiLang } from "~/utils/request-lang";

// ==================== 常量定义 ====================
const LANGUAGE_KEY = "app-language";
const CACHE_KEY = "i18n-cache";

// ==================== 语言配置 ====================
// 系统语言包（主语言：繁体中文）
export const systemLanguageMap = {
	"zh-TW": zhTw,
	en: en,
};

// 语言选项（仅支持繁体中文和英文）
export const languageOptions = [
	{ value: "zh-TW", label: "繁體中文" },
	{ value: "en", label: "English" },
];

// ==================== 缓存管理 ====================
// 仅在客户端访问 localStorage
const getCache = () => {
	if (import.meta.client && typeof window !== "undefined" && window.localStorage) {
		try {
			return JSON.parse(localStorage.getItem(CACHE_KEY) || "{}");
		} catch (e) {
			return {};
		}
	}
	return {};
};

const cache = getCache();
const pending = new Set(); // 正在翻译中的 key
const MAX_CACHE_SIZE = 1000; // 最大缓存条目数，防止内存无限增长

// 合并缓存到语言包
// 优先级：语言库配置 > 缓存翻译
function mergeCacheToMessages(locale) {
	// 先使用语言库配置（优先级最高）
	const baseMessages = { ...systemLanguageMap[locale] };
	const localePrefix = `${locale}:`;

	// 然后合并缓存翻译（仅当语言库中没有该 key 时才使用缓存）
	// 优化：直接遍历 cache，避免创建中间数组
	for (const cacheKey in cache) {
		if (cacheKey.startsWith(localePrefix)) {
			const key = cacheKey.slice(localePrefix.length);
			// 带点号的 key 对应 nested 语言包路径，不写入扁平缓存，避免覆盖 common.xxx 等嵌套结构
			if (!key || key.includes(".")) continue;
			if (!(key in baseMessages)) {
				baseMessages[key] = cache[cacheKey];
			}
		}
	}

	return baseMessages;
}

/**
 * 清理缓存（限制缓存大小，防止内存泄漏）
 */
function cleanupCache() {
	const entries = Object.entries(cache);
	if (entries.length > MAX_CACHE_SIZE) {
		// 保留最新的 MAX_CACHE_SIZE 条（简单策略：删除前 N 条）
		const toDelete = entries.slice(0, entries.length - MAX_CACHE_SIZE);
		toDelete.forEach(([key]) => delete cache[key]);
		if (import.meta.client && typeof window !== "undefined" && window.localStorage) {
			localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
		}
		if (import.meta.env.DEV) {
			console.log(`[i18n] 缓存已清理，删除了 ${toDelete.length} 条旧缓存`);
		}
	}
}

// ==================== 自动翻译功能 ====================
// i18n 实例引用（将在初始化后赋值）
let i18nInstance = null;

// 翻译队列和节流配置
const translateQueue = new Map(); // key -> { text, locale, resolve, reject }
let translateTimer = null;
let updateTimer = null; // 批量更新语言包的定时器
const TRANSLATE_DELAY = 500; // 节流延迟（毫秒）
const UPDATE_DELAY = 500; // 批量更新延迟（毫秒），减少重绘次数
const MAX_BATCH_SIZE = 50; // 每批最多翻译的文本数量

// 待更新的翻译结果（批量更新）
const pendingUpdates = new Map(); // locale -> Map<key, value>

/**
 * 批量调用 Google Translation API 进行翻译（繁体中文 → 英文）
 * @param {string[]} texts - 要翻译的文本数组
 * @param {string} locale - 目标语言
 * @returns {Promise<string[]>} 翻译结果数组
 */
async function translateBatch(texts, locale) {
	// 非英文环境，直接返回原文
	if (locale !== "en") return texts;

	if (texts.length === 0) return [];

	try {
		const url = "/v1/common/translation/center";
		const res = await fetch(url, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({
				format: "text",
				lang: "en", // 目标语言：英文
				text: texts, // 文本数组
			}),
		});

		if (!res.ok) {
			console.warn("[i18n] 翻译请求失败:", res.status, res.statusText);
			return texts; // 返回原文
		}

		const data = await res.json();

		// 调试：打印返回数据
		if (import.meta.env.DEV) {
			console.log("[i18n] 批量翻译接口返回:", data);
		}

		const { ok, results } = parseTranslationCenterResponse(data, texts);
		if (ok) {
			return results;
		}

		console.warn("[i18n] 翻译返回格式异常或翻译失败:", data);
		return texts; // 返回原文
	} catch (e) {
		console.warn("[i18n] 批量翻译失败:", e);
		return texts; // 返回原文
	}
}

/**
 * 批量更新语言包（减少重绘次数）
 */
function batchUpdateMessages() {
	if (pendingUpdates.size === 0 || !i18nInstance) return;

	// 按 locale 批量更新
	pendingUpdates.forEach((updates, locale) => {
		if (updates.size === 0) return;

		// 获取当前语言包
		const currentMessages = i18nInstance.global.messages.value[locale] || {};

		// 批量合并更新（只创建一次新对象）
		const newMessages = { ...currentMessages };
		updates.forEach((value, key) => {
			// 嵌套路径 key 只从语言包读取，不写入扁平 messages
			if (key.includes(".")) return;
			newMessages[key] = value;
		});

		// 一次性更新语言包（减少重绘）
		i18nInstance.global.setLocaleMessage(locale, newMessages);
		updates.clear();
	});

	pendingUpdates.clear();
}

/**
 * 添加待更新的翻译（延迟批量更新）
 */
function scheduleUpdate(locale, key, value) {
	if (import.meta.server) return;
	if (!pendingUpdates.has(locale)) {
		pendingUpdates.set(locale, new Map());
	}
	pendingUpdates.get(locale).set(key, value);

	// 清除之前的定时器
	if (updateTimer) {
		clearTimeout(updateTimer);
	}

	// 延迟批量更新（减少重绘次数）
	updateTimer = setTimeout(() => {
		updateTimer = null;
		batchUpdateMessages();
	}, UPDATE_DELAY);
}

/**
 * 处理翻译队列（节流批量处理）
 */
async function processTranslateQueue() {
	if (translateQueue.size === 0) return;

	// 按 locale 分组（优化：减少对象创建）
	const queueByLocale = new Map();
	const queueItems = [];

	translateQueue.forEach((item, cacheKey) => {
		const { locale, key, text, resolve, reject } = item;
		if (!queueByLocale.has(locale)) {
			queueByLocale.set(locale, []);
		}
		queueByLocale.get(locale).push({ cacheKey, key, text, resolve, reject });
		queueItems.push({ cacheKey, locale });
	});

	// 清空队列（提前清空，避免重复处理）
	translateQueue.clear();

	// 处理每个语言组的翻译
	for (const [locale, items] of queueByLocale) {
		// 分批处理，每批最多 MAX_BATCH_SIZE 个
		for (let i = 0; i < items.length; i += MAX_BATCH_SIZE) {
			const batch = items.slice(i, i + MAX_BATCH_SIZE);
			const texts = batch.map(item => item.text);

			try {
				const results = await translateBatch(texts, locale);

				// 批量更新缓存（减少 localStorage 写入次数）
				const cacheUpdates = {};
				const successfulTranslations = [];

				// 分发翻译结果
				batch.forEach((item, index) => {
					const result = results[index] || item.text;
					const { cacheKey, key } = item;

					// 如果翻译成功（结果与原文不同），保存到缓存
					if (result !== item.text) {
						cacheUpdates[cacheKey] = result;
						successfulTranslations.push({ key, result });
					} else {
						// 翻译失败，显示原文
						successfulTranslations.push({ key, result: key });
					}

					// 从 pending 中移除
					pending.delete(cacheKey);

					// 调用 resolve
					if (item.resolve) {
						item.resolve(result);
					}
				});

				// 批量更新缓存（一次性写入 localStorage）
				if (Object.keys(cacheUpdates).length > 0) {
					Object.assign(cache, cacheUpdates);
					if (import.meta.client && typeof window !== "undefined" && window.localStorage) {
						localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
					}

					// 定期清理缓存
					if (Object.keys(cache).length > MAX_CACHE_SIZE) {
						cleanupCache();
					}

					if (import.meta.env.DEV) {
						console.log(`[i18n] ✅ 批量翻译完成: ${successfulTranslations.length} 条`);
					}
				}

				// 批量更新语言包（延迟更新，减少重绘）
				successfulTranslations.forEach(({ key, result }) => {
					scheduleUpdate(locale, key, result);
				});
			} catch (error) {
				// 批量处理错误
				batch.forEach(item => {
					const { cacheKey, key, reject } = item;
					pending.delete(cacheKey);
					scheduleUpdate(locale, key, key); // 显示原文
					if (reject) {
						reject(error);
					}
				});
			}
		}
	}
}

/**
 * 添加翻译任务到队列（节流）
 * @param {string} key - 翻译键
 * @param {string} text - 要翻译的文本
 * @param {string} locale - 目标语言
 * @returns {Promise<string>} 翻译结果
 */
function addToTranslateQueue(key, text, locale) {
	return new Promise((resolve, reject) => {
		const cacheKey = `${locale}:${key}`;
		translateQueue.set(cacheKey, {
			key,
			text,
			locale,
			resolve,
			reject,
		});

		// 清除之前的定时器
		if (translateTimer) {
			clearTimeout(translateTimer);
		}

		// 设置新的定时器（节流）
		translateTimer = setTimeout(() => {
			translateTimer = null;
			processTranslateQueue();
		}, TRANSLATE_DELAY);

		// 如果队列已满，立即处理一批
		if (translateQueue.size >= MAX_BATCH_SIZE) {
			if (translateTimer) {
				clearTimeout(translateTimer);
				translateTimer = null;
			}
			processTranslateQueue();
		}
	});
}

/**
 * 從嵌套語言包按路徑取值（如 corporateCenter.indexPc.section1.challenge.0.title）
 * @param {Record<string, unknown>} root
 * @param {string} key
 * @returns {string | undefined}
 */
function getMessageByPath(root, key) {
	if (!key || !root || typeof root !== "object") return undefined;
	const parts = key.split(".");
	let cur = root;
	for (const p of parts) {
		if (cur == null) return undefined;
		if (Array.isArray(cur)) {
			const idx = Number(p);
			if (!Number.isInteger(idx) || idx < 0 || idx >= cur.length) return undefined;
			cur = cur[idx];
		} else if (typeof cur === "object") {
			cur = cur[p];
		} else {
			return undefined;
		}
	}
	return typeof cur === "string" ? cur : undefined;
}

/**
 * 处理缺失的翻译（仅在英文环境下自动翻译）
 * 当 en.js 中没有配置的文本，会自动翻译并缓存
 * 注意：vue-i18n 的 handleMissing 回调中 locale 可能是 'en' 或 'zh'，需要统一处理
 * 优先级：语言库配置 > 缓存翻译 > 自动翻译
 */
function handleMissing(locale, key, vm, values) {
	const normalizedLocale = locale === "zh" ? "zh-TW" : locale;

	// 始终优先从最新语言包读取（支持嵌套 key）
	const msgs = systemLanguageMap[normalizedLocale] || {};
	const fromPkg = getMessageByPath(msgs, key);
	if (fromPkg !== undefined) {
		return fromPkg;
	}

	if (import.meta.server) {
		return key;
	}

	if (normalizedLocale !== "en" || !key) {
		return key;
	}

	// 缓存 key 使用标准化的语言代码
	const cacheKey = `${normalizedLocale}:${key}`;

	// 已有缓存，直接返回（缓存优先级低于语言库配置）
	if (cache[cacheKey]) {
		if (import.meta.env.DEV) {
			console.log(`[i18n] 使用缓存: "${key}" → "${cache[cacheKey]}"`);
		}
		return cache[cacheKey];
	}

	// 正在翻译中，显示 '...'
	if (pending.has(cacheKey)) {
		return "...";
	}

	// 开始异步翻译（加入队列，批量处理）
	pending.add(cacheKey);

	if (import.meta.env.DEV) {
		console.warn(`[i18n] 缺失翻译: "${key}"，已加入翻译队列...`);
	}

	// 先显示 '...' 表示正在翻译（使用批量更新）
	scheduleUpdate(normalizedLocale, key, "...");

	// 添加到翻译队列（节流批量处理）
	addToTranslateQueue(key, key, normalizedLocale).catch(err => {
		if (import.meta.env.DEV) {
			console.error(`[i18n] 翻译异常: "${key}"`, err);
		}
		// 翻译失败，显示原文（使用批量更新）
		scheduleUpdate(normalizedLocale, key, key);
		pending.delete(cacheKey);
	});

	return "...";
}

// ==================== 语言切换功能 ====================
/** @type {import('vue').Ref<string> | null} Nuxt useCookie，由 plugins/i18n.js 注入 */
let localeCookieRef = null;

/** 客户端 hydration 后待应用的语言（localStorage 与 cookie 不一致时） */
let pendingClientLocale = null;

export function initLocaleCookie(cookieRef) {
	localeCookieRef = cookieRef;
}

export function consumePendingClientLocale() {
	const pending = pendingClientLocale;
	pendingClientLocale = null;
	return pending;
}

function readLocalStorageLanguage() {
	if (!import.meta.client || typeof window === "undefined" || !window.localStorage) {
		return null;
	}
	try {
		const saved = localStorage.getItem(LANGUAGE_KEY);
		return saved && systemLanguageMap[saved] ? saved : null;
	} catch {
		return null;
	}
}

function readCookieLanguage() {
	const saved = localeCookieRef?.value;
	return saved && systemLanguageMap[saved] ? saved : null;
}

/** 读取用户保存的语言偏好（cookie 优先，与 SSR 一致） */
export function readStoredLanguagePreference() {
	return readCookieLanguage() || readLocalStorageLanguage() || "zh-TW";
}

/** 客户端运行时 locale（vue-i18n），切换语言时比 cookie 更及时 */
export function getRuntimeLocale() {
	const runtime = i18nInstance?.global?.locale?.value;
	if (runtime && systemLanguageMap[runtime]) {
		return runtime;
	}
	return readStoredLanguagePreference();
}

/**
 * 按路由解析应使用的语言：URL 前缀 /tw|/en 优先；无前缀时默认中文，或读 cookie / localStorage
 * @param {string} [path]
 */
export function resolveAppLocale(path) {
	const fromPath = path ? parseLocaleFromPath(path) : null;
	if (fromPath) return fromPath;
	if (!path || !isI18nLocaleRoute(path)) {
		return "zh-TW";
	}
	// 无前缀的多语言路由：默认中文；若用户偏好英文由 locale-router 补 /en
	const stored = readStoredLanguagePreference();
	return stored === "en" ? "en" : "zh-TW";
}

/**
 * 与 SSR 一致的语言：URL 前缀优先；无前缀默认中文（英文偏好由客户端补 /en）
 * @param {string} path
 */
export function resolveSsrLocale(path) {
	const fromPath = path ? parseLocaleFromPath(path) : null;
	if (fromPath) return fromPath;
	if (!path || !isI18nLocaleRoute(path)) {
		return "zh-TW";
	}
	const cookieLang = readCookieLanguage();
	return cookieLang === "en" ? "en" : "zh-TW";
}

/**
 * 客户端首屏 hydration 用：必须与 SSR 输出一致；localStorage/cookie 差异延后到 mounted
 * @param {string} path
 * @param {string} [ssrLocaleFromPayload] SSR payload 中的语言（优先，避免客户端 cookie 与 SSR 请求不一致）
 */
export function resolveHydrationLocale(path, ssrLocaleFromPayload) {
	const ssrLocale =
		ssrLocaleFromPayload && systemLanguageMap[ssrLocaleFromPayload]
			? ssrLocaleFromPayload
			: resolveSsrLocale(path);
	if (!import.meta.client) {
		return ssrLocale;
	}
	// URL 已显式带 /tw|/en 时以 URL 为准；无前缀默认中文
	if (parseLocaleFromPath(path)) {
		return ssrLocale;
	}
	const stored = readLocalStorageLanguage();
	const cookieLang = readCookieLanguage();
	const preferred = stored || cookieLang;
	if (preferred && preferred !== ssrLocale) {
		pendingClientLocale = preferred;
	}
	return ssrLocale;
}

/**
 * 获取当前语言（默认繁体中文）
 * @param {string} [path] 传入路由 path 时可按企业中心规则解析
 */
export function getLanguage(path) {
	if (path) {
		return resolveAppLocale(path);
	}
	return readStoredLanguagePreference();
}

/**
 * 设置语言
 * @param {string} lang
 * @param {{ persist?: boolean }} [options] persist 为 false 时仅改运行时 locale，不写 localStorage（用于离开企业中心等场景）
 */
export function setLanguage(lang, options = {}) {
	const { persist = true } = options;
	if (!systemLanguageMap[lang]) return;

	if (persist) {
		if (localeCookieRef) {
			localeCookieRef.value = lang;
		}
		if (import.meta.client && typeof window !== "undefined" && window.localStorage) {
			localStorage.setItem(LANGUAGE_KEY, lang);
			// App 内嵌场景会写入 app_lang，切换语言时同步更新
			if (localStorage.getItem("app_lang")) {
				localStorage.setItem("app_lang", localeToApiLang(lang));
			}
		}
	}

	// 重新合并语言包（系统语言包 + 缓存）
	if (!i18nInstance) return;
	refreshAllLocaleMessages();
	if (i18nInstance.global.locale.value !== lang) {
		i18nInstance.global.locale.value = lang;
	}

	if (persist && import.meta.client) {
		try {
			const route = useRoute();
			if (isI18nLocaleRoute(route.path)) {
				const nextFullPath = switchLocaleInFullPath(route.fullPath, lang);
				if (nextFullPath !== route.fullPath) {
					return navigateTo(nextFullPath, { replace: true });
				}
			}
		} catch {
			// 非 Nuxt 上下文（如单测）忽略路由跳转
		}
	}
}

// ==================== 开发工具 ====================
/**
 * 导出缓存（查看格式化的缓存）
 * 使用：__i18n__.exportCache()
 */
export function exportCache() {
	const result = {};
	Object.entries(cache).forEach(([k, v]) => {
		const [locale, text] = k.split(":");
		if (!result[locale]) result[locale] = {};
		result[locale][text] = v;
	});
	console.log("[i18n] 缓存:", JSON.stringify(result, null, 2));
	return result;
}

/**
 * 一键复制到语言库文件（仅英文缓存）
 * 使用：__i18n__.copyToLangFile()
 * 功能：将缓存的英文翻译合并到现有配置，生成可直接粘贴到 en.js 的代码
 */
export function copyToLangFile() {
	const enCache = {};

	// 只提取英文缓存
	Object.entries(cache).forEach(([k, v]) => {
		const [locale, text] = k.split(":");
		if (locale === "en" && text) {
			enCache[text] = v;
		}
	});

	if (Object.keys(enCache).length === 0) {
		console.warn("[i18n] ⚠️ 没有英文缓存可导出");
		return "";
	}

	// 读取现有的 en.js 文件内容
	const existing = systemLanguageMap["en"] || {};

	// 合并现有配置和缓存（缓存优先，覆盖现有配置）
	const merged = { ...existing, ...enCache };

	// 转义字符串中的特殊字符
	function escapeString(str) {
		if (typeof str !== "string") return str;
		return str
			.replace(/\\/g, "\\\\") // 反斜杠
			.replace(/'/g, "\\'") // 单引号
			.replace(/\n/g, "\\n") // 换行符
			.replace(/\r/g, "\\r") // 回车符
			.replace(/\t/g, "\\t"); // 制表符
	}

	// 生成可以直接复制到 en.js 的代码（按 key 排序）
	const entries = Object.entries(merged)
		.sort(([a], [b]) => a.localeCompare(b))
		.map(([key, value]) => {
			const escapedValue = escapeString(value);
			return `  ${key}: '${escapedValue}',`;
		});

	const code = `export default {\n${entries.join("\n")}\n};`;

	// 复制到剪贴板（如果支持）
	if (navigator.clipboard && navigator.clipboard.writeText) {
		navigator.clipboard
			.writeText(code)
			.then(() => {
				console.log("[i18n] ✅ 已复制到剪贴板！");
				console.log("[i18n] 📝 请粘贴到 src/i18n/lang/en.js 文件中");
				console.log(
					`[i18n] 📊 共 ${Object.keys(merged).length} 条翻译（${Object.keys(enCache).length} 条来自缓存）`
				);
				console.log("--- 代码开始 ---");
				console.log(code);
				console.log("--- 代码结束 ---");
			})
			.catch(err => {
				console.error("[i18n] ❌ 复制失败:", err);
				console.log("--- 请手动复制以下代码 ---");
				console.log(code);
				console.log("--- 代码结束 ---");
			});
	} else {
		console.log("--- 请手动复制以下代码到 src/i18n/lang/en.js ---");
		console.log(
			`[i18n] 📊 共 ${Object.keys(merged).length} 条翻译（${Object.keys(enCache).length} 条来自缓存）`
		);
		console.log(code);
		console.log("--- 代码结束 ---");
	}

	return code;
}

/**
 * 清除缓存
 * 使用：__i18n__.clearCache()
 */
export function clearCache() {
	if (import.meta.client && typeof window !== "undefined" && window.localStorage) {
		localStorage.removeItem(CACHE_KEY);
	}
	Object.keys(cache).forEach(k => delete cache[k]);
	console.log("[i18n] 缓存已清除");
}

// ==================== i18n 实例初始化 ====================
// 创建 i18n 实例：客户端單例；服務端每次新建（避免併發 SSR 串台）
/** @param {string} [initialLang] 由 plugins/i18n 按路由与 cookie 传入，避免 SSR/客户端不一致 */
/** 从语言包重新合并 messages（HMR / 新增 fragment 后刷新） */
function refreshAllLocaleMessages() {
	if (!i18nInstance) return;
	i18nInstance.global.setLocaleMessage("zh-TW", mergeCacheToMessages("zh-TW"));
	i18nInstance.global.setLocaleMessage("en", mergeCacheToMessages("en"));
}

export function createI18nInstance(initialLang) {
	if (import.meta.client && i18nInstance) {
		refreshAllLocaleMessages();
		if (initialLang && i18nInstance.global.locale.value !== initialLang) {
			setLanguage(initialLang, { persist: false });
		}
		return i18nInstance;
	}

	const lang = initialLang && systemLanguageMap[initialLang] ? initialLang : "zh-TW";

	const initialMessages = {
		"zh-TW": mergeCacheToMessages("zh-TW"),
		en: mergeCacheToMessages("en"),
	};

	const instance = createI18n({
		legacy: false,
		locale: lang,
		fallbackLocale: false, // 禁用 fallback，由 missing 处理
		messages: initialMessages,
		silentTranslationWarn: true,
		silentFallbackWarn: true,
		fallbackWarn: false,
		missingWarn: false,
		missing: handleMissing, // 处理缺失翻译（自动翻译）
	});

	if (import.meta.client) {
		i18nInstance = instance;
		i18n = instance;
	}

	return instance;
}

// 全局 t() 使用的实例引用（仅由 plugin / createI18nInstance 赋值，避免模块加载时抢先读 localStorage）
let i18n = null;

// ==================== 全局翻译函数 ====================
/**
 * 全局翻译函数
 * 可在模板中使用 $t()，在 script setup 中直接使用 t()
 * @param {string} key - 翻译键
 * @param {...any} args - 翻译参数
 * @returns {string} 翻译结果
 */
export const t = (key, ...args) => {
	if (!i18n && import.meta.client) {
		i18n = createI18nInstance();
	}
	if (i18n) {
		return i18n.global.t(key, ...args);
	}
	return key; // 服务端返回 key
};

// ==================== 开发工具（仅开发环境） ====================
if (import.meta.env.DEV && import.meta.client) {
	window.__i18n__ = {
		exportCache, // 查看格式化的缓存
		copyToLangFile, // 一键复制到语言库文件（推荐）
		clearCache, // 清除缓存
		cache, // 原始缓存对象
		t, // 全局翻译函数
	};
	console.log("[i18n] 开发工具已加载，使用 __i18n__.copyToLangFile() 一键复制到语言库");
}

export default i18n;
